import styles from './index.module.scss';
import { fabric } from 'fabric';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useContext } from 'react';
import useCanvasZoom from '../hooks/useCanvasZoom';
import useCanvasMovement from '../hooks/useCanvasMovement';
import useRectMovement from '../hooks/useRectMovement';
import { RESIZE_IMG_CANVASNAME } from '@utils/CONST'
import Utils from '@utils/utilsEdit';
import { ImageResizeContext } from '../../imageResizeContext'
import { IMAGE_TYPE } from '@utils/CONST'

const CanvasRightComCrop = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        setPosition: (position) => {
            if (canvasInstance.current) {
                canvasInstance.current.viewportTransform[4] += position.left
                canvasInstance.current.viewportTransform[5] += position.top
                rectRef.current.setCoords()
                canvasInstance.current.requestRenderAll();
            }
            if (bgCanvasInstance.current) {
                bgCanvasInstance.current.viewportTransform[4] += position.left
                bgCanvasInstance.current.viewportTransform[5] += position.top
                bgCanvasInstance.current.requestRenderAll();
            }
        },
        cropImageByRegion
    }))

    const { imageUrl, onZoom, zoom, initMinZoom, rectWidth, rectHeight, onMove } = props
    const canvasRef = useRef(null);
    const bgCanvasRef = useRef(null);
    const canvasInstance = useRef(null);
    const bgCanvasInstance = useRef(null);
    const [imgWidth, setImgWidth] = useState()
    const [imgHeight, setImgHeight] = useState()
    const rectRef = useRef()
    const imgRef = useRef()
    const isMounted = useRef()
    const {
        targetSsize,
        setHandleLoading,
        setDownBtnLoading,
        saveResultToProject,
        AIEditTemTaskId
    } = useContext(ImageResizeContext)

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])


    const getRectOffsetFromImage = (rect, image) => {
        if (!rect || !image) return;

        const offsetX = rect.left - image.left;
        const offsetY = rect.top - image.top;

        // console.log('矩形左上角相对图片左上角的实际像素偏移：', offsetX, offsetY);
        return { offsetX, offsetY };
    };


    const initCanvas = () => {
        if (!canvasRef.current?.parentElement || !bgCanvasRef.current) return;
        const container = canvasRef.current.parentElement;
        const { width, height } = container.getBoundingClientRect();
        setHandleLoading(true)

        if (canvasInstance.current) {
            canvasInstance.current.dispose();
        }

        canvasInstance.current = new fabric.Canvas(canvasRef.current, {
            width,
            height,
            backgroundColor: 'transparent',
            selectable: false,
            defaultCursor: 'grab',
            hoverCursor: 'grab',
            moveCursor: 'grabbing',
        });
        bgCanvasInstance.current = new fabric.Canvas(bgCanvasRef.current, {
            width,
            height,
            selection: false,
            evented: false,
            interactive: false,
            defaultCursor: 'grab',
            hoverCursor: 'grab',
            moveCursor: 'grabbing',
        });
    };

    const loadImage = (url) => {
        if (!canvasInstance.current || !bgCanvasInstance.current || !canvasRef.current?.parentElement) return;

        fabric.Image.fromURL(url, (img) => {
            if (!canvasRef.current?.parentElement || !canvasInstance.current || !bgCanvasInstance.current) return;
            const container = canvasRef.current.parentElement;
            const originalWidth = img.width;
            const originalHeight = img.height;
            setImgWidth(originalWidth)
            setImgHeight(originalHeight)
            // 计算基于容器短边的缩放比例
            const containerRatio = container.clientWidth / container.clientHeight;
            const imageRatio = originalWidth / originalHeight;
            let scale = 1;
            if (containerRatio > imageRatio) {
                scale = container.clientHeight / originalHeight;
            } else {
                scale = container.clientWidth / originalWidth;
            }
            // 第一次先获取缩放倍数，传导父组件比较后再往下渲染
            onZoom(scale, RESIZE_IMG_CANVASNAME.RIGHT_CANVAS_NAME)
            if (!initMinZoom) return
            const leftPoint = (container.clientWidth - originalWidth * initMinZoom) / 2
            const topPoint = (container.clientHeight - originalHeight * initMinZoom) / 2

            img.set({
                left: leftPoint,
                top: topPoint,
                selectable: false,
                evented: false,
                hasControls: false,
                hasBorders: false,
                lockMovementX: true,
                lockMovementY: true,
                lockRotation: true,
                lockScalingX: true,
                lockScalingY: true,
                hoverCursor: 'grab',
                moveCursor: 'grabbing',
                type: 'image',
            })
            canvasInstance.current.add(img);
            imgRef.current = img

            bgCanvasInstance.current.setBackgroundImage(img, () => {
                bgCanvasInstance.current.renderAll()
            })
            bgCanvasInstance.current.zoomToPoint({ x: leftPoint, y: topPoint }, initMinZoom);

            const mask = new fabric.Rect({
                left: leftPoint,
                top: topPoint,
                width: img.width,
                height: img.height,
                selectable: false,
                evented: false,
                fill: 'rgba(255,255,255,0.7)',
                hasControls: false,
                lockRotation: true,
                hoverCursor: 'grab',
                moveCursor: 'grabbing',
                type: 'mask',
            });
            canvasInstance.current.add(mask);

            const rect = new fabric.Rect({
                left: img.left + (img.width - rectWidth) / 2,
                top: img.top + (img.height - rectHeight) / 2,
                width: rectWidth,
                height: rectHeight,
                selectable: true,
                hasControls: false,
                lockRotation: true,
                type: 'rect',
                globalCompositeOperation: 'destination-out',
                hoverCursor: 'grab',
                moveCursor: 'grabbing',
            });
            canvasInstance.current.add(rect);
            rectRef.current = rect


            canvasInstance.current.zoomToPoint({ x: leftPoint, y: topPoint }, initMinZoom);
            getRectOffsetFromImage(rect, img)
            setHandleLoading(false)

            canvasInstance.current.renderAll();
            bgCanvasInstance.current.renderAll();
        });
    };

    // 处理窗口大小变化
    useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            if (!canvasInstance.current || !bgCanvasInstance.current) return;


            const { width, height } = entries[0].contentRect;
            canvasInstance.current.setDimensions({ width, height });
            bgCanvasInstance.current.setDimensions({ width, height });
            canvasInstance.current.requestRenderAll();
            bgCanvasInstance.current.requestRenderAll();
        });

        const parentDiv = canvasRef.current?.parentElement;
        if (parentDiv) {
            resizeObserver.observe(parentDiv);
        }
    }, []);

    useEffect(() => {
        initCanvas();
        loadImage(imageUrl);
    }, [imageUrl, initMinZoom]);

    useRectMovement(
        canvasInstance,
        () => { }
    )

    useCanvasMovement(canvasInstance, (pos) => {
        if (bgCanvasInstance.current) {
            bgCanvasInstance.current.viewportTransform[4] += pos.left
            bgCanvasInstance.current.viewportTransform[5] += pos.top
            bgCanvasInstance.current.requestRenderAll();
        }
        if (onMove) onMove(pos)
    })

    // 处理缩放
    useCanvasZoom(
        canvasInstance,
        bgCanvasInstance,
        zoom,
        onZoom,
        initMinZoom,
        RESIZE_IMG_CANVASNAME.SCROLL_CANVAS
    )


    const cropImageByRegion = async (type) => {
        setDownBtnLoading(true)
        const { offsetX, offsetY } = await getRectOffsetFromImage(rectRef.current, imgRef.current)
        try {
            const croppedImageDataBase64 = await Utils.cropImageByRegion({
                imageUrl: imageUrl,
                offsetX: offsetX,
                offsetY: offsetY,
                width: rectWidth,
                height: rectHeight,
                format: type, // png、jpeg
                fileName: `PSAI-${Date.now()}`
            });
            const croppedImageDataURL = await Utils.uploadBase64Image(croppedImageDataBase64, true)
            const res2 = await saveResultToProject(imageUrl, croppedImageDataURL, '', IMAGE_TYPE.IMAGE_RESIZE, AIEditTemTaskId)
            if (!res2) return
        } catch (err) {
            console.error('裁剪失败', err)
        } finally {
            setDownBtnLoading(false)
        }
    }

    return (
        <div className={styles.canvasRightComCrop}>
            <span className={styles.sizeTips}>结果图：{Math.min(targetSsize[1], 8192)} * {Math.min(targetSsize[0], 8192)}px</span>
            <canvas ref={bgCanvasRef} />
            <canvas ref={canvasRef} />
        </div>
    );
})
export default CanvasRightComCrop;
